import { useState } from "react";

import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import Stack from "@mui/material/Stack";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";
import { ItemSelectionToggle } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/ItemSelectionToggle";
import { AgenticLogicSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/AgenticLogicSection";
import { BundleDiscountSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/BundleDiscountSection";
import { CopySection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/CopySection";
import { GeneralSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/GeneralSection";
import { RulesSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/RulesSection";
import { UpsellPlacementSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/UpsellPlacementSection";
import { useIsAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAgenticLogicEnabled";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import {
  bundleDiscountStructure,
  bundleMinimum,
  rebaseTiers,
} from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsell/bundleTiers";
import type { BundleOnsiteUpsellComponent } from "@src/store/slices/experienceBuilder/types";
import { assert } from "@src/utils/assert";

export const BundleOnsiteUpsellComponentPanel = () => {
  const dispatch = useDispatch();
  const agenticLogicEnabled = useIsAgenticLogicEnabled();

  const mod = useSelector(Selector.component.getVariationDraft) as
    | BundleOnsiteUpsellComponent
    | undefined;
  assert(mod !== undefined, "mod is undefined");
  const isApplicable = useSelector(Selector.isVariationDraftApplicable);
  const errors = useSelector(Selector.getVariationDraftUIErrors);
  const [structure, setStructure] = useState(() =>
    bundleDiscountStructure(mod.discount)
  );

  const ruleMissing = agenticLogicEnabled && mod.agenticLogicRuleId == null;

  const update = (changes: Partial<BundleOnsiteUpsellComponent>) =>
    dispatch(Action.component.setDraft({ ...mod, ...changes }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  // Tier rows run up to the product count.
  const changeMaxProducts = (maxProducts: number) =>
    update({
      maxProducts,
      discount: mod.discount && {
        ...mod.discount,
        discountTiers: rebaseTiers(mod.discount.discountTiers, {
          minimum: bundleMinimum(mod.discount),
          maxProducts,
          structure,
        }),
      },
    });

  return (
    <>
      <ModificationHeader />
      <Panel>
        <Panel.Body>
          <Stack spacing={5}>
            <UpsellPlacementSection
              mod={mod}
              errors={errors}
              onChange={update}
            />

            <IgDivider />

            <CopySection
              copy={mod.copy}
              errors={errors}
              onChange={(copy) => update({ copy })}
            />

            <IgDivider />

            <GeneralSection
              location={mod.location}
              showExclusions={!agenticLogicEnabled}
              maxProducts={mod.maxProducts}
              excludeOutOfStock={mod.excludeOutOfStock}
              excludeItemsInCart={mod.excludeItemsInCart}
              excludePdpProduct={mod.excludePdpProduct}
              onChange={({ maxProducts, ...changes }) =>
                maxProducts === undefined
                  ? update(changes)
                  : changeMaxProducts(maxProducts)
              }
              showMaxProducts={!agenticLogicEnabled}
            >
              <ItemSelectionToggle
                checked={mod.allowItemSelection}
                onChange={(allowItemSelection) =>
                  update({ allowItemSelection })
                }
              />
            </GeneralSection>

            <IgDivider />

            <BundleDiscountSection
              discount={mod.discount}
              structure={structure}
              maxProducts={mod.maxProducts}
              errors={errors}
              onChange={(discount) => update({ discount })}
              onStructureChange={setStructure}
            />

            <IgDivider />

            <Panel.Header>Rules</Panel.Header>
            {agenticLogicEnabled ? (
              <AgenticLogicSection mod={mod} onChange={update} />
            ) : (
              <RulesSection
                rules={mod.rules}
                location={mod.location}
                onChange={(rules) => update({ rules })}
              />
            )}
          </Stack>
        </Panel.Body>

        <Panel.Buttons>
          <Panel.CancelButton onClick={() => void cancel()} />
          <Panel.ApplyButton
            onClick={apply}
            disabled={!isApplicable || ruleMissing}
          />
        </Panel.Buttons>
      </Panel>
    </>
  );
};
